import { AuthenticatedHttp } from "./http.service";
import { Injectable } from "@angular/core";
import { HttpResponseHandlerService } from "./httpResponseHandler.service";
import { AppConstants } from "../app.constants";
import { JwtHelperService } from "@auth0/angular-jwt";
import { HttpModel } from "../models/http.model";
import { BehaviorSubject, Observable, pipe } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AuthoritySettings } from "../models/AuthoritySettings";
import { OidcClientService } from "./oidcClient.service";
import { User } from "oidc-client";
import { EToolMode } from "../models/toolMode.enum";
import { HttpParams } from "@angular/common/http";

@Injectable()
export class AuthService {
  jwtHelper: JwtHelperService = new JwtHelperService();
  $loggedIn = new BehaviorSubject<boolean>(false);

  constructor(private authHttp: AuthenticatedHttp, private httpResponseHandlerService: HttpResponseHandlerService, private oidcClientService: OidcClientService) {
    this.$loggedIn.next(!this.jwtHelper.isTokenExpired(this.getToken()));
  }

  getExternalAuthorities(): Observable<{ authorityId: string, clientSettings: AuthoritySettings }[]> {
    return this.authHttp.get<{ authorityId: string, clientSettings: AuthoritySettings }[]>(AppConstants.AUTH_GET_AUTHORITY_LIST)
      .pipe(
        map((response) => this.httpResponseHandlerService.handleGetResponse(response, false)),
        catchError((error: any) => this.httpResponseHandlerService.handleError(error))
      );
  }

  login(username: string, password: string): Observable<any> {
    return this.authHttp.post<any>(AppConstants.LOGIN, {
      username,
      password
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          if (response.accessToken) {
            this.setAuth(response.accessToken);
          }
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error: any) => {
          return this.httpResponseHandlerService.handleError(error);
        })
      );
  }

  requestLoginToken(mode: number): Observable<any> {
    const params: HttpParams = new HttpParams()
      .append("mode", mode.toString());
    return this.authHttp.get(AppConstants.REQUEST_LOGIN_TOKEN, params).pipe(
      map((response) => {
        return this.httpResponseHandlerService.handleGetResponse(response);
      }),
      catchError((error) => {
        return this.httpResponseHandlerService.handleError(error);
      })
    );
  }

  submitTotp(userId: string, code: string): Observable<any> {
    return this.authHttp.post<any>(AppConstants.SUBMIT_TOTP_CODE, {
      userId,
      code
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          if (response.accessToken) {
            this.setAuth(response.accessToken);
          }
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error) => {
          return this.httpResponseHandlerService.handleError(error);
        })
      );
  }

  setup2FactorAuthentication(algorithm: number): Observable<any> {
    return this.authHttp.post<any>(AppConstants.AUTH_SETUP_MULTIFACTOR, {
      mode: "totp",
      hashAlgorithm: algorithm
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error) => {
          return this.httpResponseHandlerService.handleError(error);
        })
      );
  }

  confirm2FactorAuthentication(code: string, userId: string): Observable<any> {
    return this.authHttp.post<any>(AppConstants.AUTH_CONFIRM_MULTIFACTOR, {
      code,
      userId
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error) => {
          return this.httpResponseHandlerService.handleError(error);
        })
      );
  }

  getIsMultifactorAuthenticationActive() {
    return this.authHttp.get<any>(AppConstants.AUTH_GET_MULTIFACTOR)
      .pipe(
        map((response) => this.httpResponseHandlerService.handleGetResponse(response, false)),
        catchError((error: any) => this.httpResponseHandlerService.handleError(error))
      );
  }

  deactivateMultifactorAuthentication() {
    return this.authHttp.post<any>(AppConstants.AUTH_DEACTIVATE_MULTIFACTOR, {})
      .pipe(
        map((response) => this.httpResponseHandlerService.handlePostResponse(response, false)),
        catchError((error: any) => this.httpResponseHandlerService.handleError(error))
      );
  }

  tokenLogin(token: string) {
    return this.authHttp.post<any>(AppConstants.LOGIN + "/proxy", {
      token
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          if (response.accessToken) {
            this.setAuth(response.accessToken);
          }
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error: any) => {
          return this.httpResponseHandlerService.handleError(error);
        })
      );
  }

  loginSSO(accessToken: string, authorityId: string, resend: boolean = true): Observable<any> {
    return this.authHttp.post<any>(AppConstants.AUTH_LOGIN_SSO, {
      token: accessToken,
      authorityId
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => {
          if (response.accessToken) {
            this.setAuth(response.accessToken);
          }
          return this.httpResponseHandlerService.handlePostResponse(response, false);
        }),
        catchError((error: any) => {
          if (error.status === 401 && resend) {
            this.oidcClientService.renewToken().then((user: User) => {
              return this.loginSSO(user.access_token, authorityId, false);
            });
          } else {
            return this.httpResponseHandlerService.handleError(error);
          }
          return null;
        })
      );
  }

  registerSSO(accessToken: string, authorityId: string, firstName: string, lastName: string, gender: number): Observable<any> {
    return this.authHttp.post<any>(AppConstants.AUTH_REGISTER_SSO, {
      token: accessToken,
      authorityId,
      firstName,
      lastName,
      gender
    }, null, "post", "application/json", false, false)
      .pipe(
        map((response) => this.httpResponseHandlerService.handlePostResponse(response, false)),
        catchError((error: any) => this.httpResponseHandlerService.handleError(error))
      );
  }

  logout(): void {
    this.setAuth(null);
  }

  setAuth(auth: string) {
    if (auth) {
      localStorage.setItem(AppConstants.AUTH_KEY, JSON.stringify(auth));
      this.$loggedIn.next(true);
    } else {
      localStorage.removeItem(AppConstants.AUTH_KEY);
      this.$loggedIn.next(false);
    }
  }

  getToken(): any {
    const authObject = localStorage.getItem(AppConstants.AUTH_KEY);
    if (authObject) {
      return JSON.parse(authObject);
    } else {
      return null;
    }
  }

  getDecodedToken() {
    const token = this.getToken();
    if (token) {
      return this.jwtHelper.decodeToken(token);
    }
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    return this.jwtHelper.isTokenExpired(token);
  }

  getModes(): EToolMode[] {
    const decodedToken = this.getDecodedToken();
    if (decodedToken && decodedToken.modes) {
      return decodedToken.modes.split(";")
        .map((m) => parseInt(m, 10) as EToolMode);
    } else {
      return [];
    }
  }

  requestPasswordReset(resetPasswordUsername: string) {
    return this.authHttp.post<HttpModel>(AppConstants.REQEUST_PASSWORD_RESET, {
      requesterEmailAdress: resetPasswordUsername
    })
      .pipe(
        map((response) => this.httpResponseHandlerService.handlePostResponse(response, true)),
        catchError((error) => this.httpResponseHandlerService.handleError(error, true))
      );
  }
}
